import { createContext, useContext, useEffect, useState } from "react";

const KEY_LANG = "mh_lang_v1";
const KEY_CUR = "mh_currency_v1";
const KEY_ASKED = "mh_prefs_asked_v1";

// Simple fixed exchange rate for display purposes (base = CAD)
export const CAD_TO_USD = 0.73;

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const [language, setLanguage] = useState(() => localStorage.getItem(KEY_LANG) || "fr");
  const [currency, setCurrency] = useState(() => localStorage.getItem(KEY_CUR) || "cad");
  const [showPicker, setShowPicker] = useState(() => !localStorage.getItem(KEY_ASKED));

  useEffect(() => { localStorage.setItem(KEY_LANG, language); }, [language]);
  useEffect(() => { localStorage.setItem(KEY_CUR, currency); }, [currency]);

  const confirmPicker = (lang, cur) => {
    setLanguage(lang);
    setCurrency(cur);
    localStorage.setItem(KEY_ASKED, "1");
    setShowPicker(false);
  };

  const openPicker = () => setShowPicker(true);

  return (
    <PreferencesContext.Provider value={{
      language, setLanguage, currency, setCurrency,
      showPicker, confirmPicker, openPicker,
    }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export const usePrefs = () => useContext(PreferencesContext);

// Currency formatting helper
export function formatPrice(cadAmount, currency = "cad", language = "fr") {
  const amount = currency === "usd" ? cadAmount * CAD_TO_USD : cadAmount;
  const symbol = currency === "usd" ? "US$" : "$";
  const suffix = currency === "usd" ? "USD" : "CAD";
  return { symbol, amount, suffix, display: `${symbol}${amount.toFixed(2)}` };
}

// Minimal translations for high-visibility strings
const T = {
  fr: {
    "nav.catalog": "Catalogue", "nav.artists": "Pilotes", "nav.hoodies": "Hoodies",
    "nav.tshirts": "T-Shirts", "nav.limited": "Éditions Ltd.",
    "nav.orders": "Commandes", "nav.studio": "Studio", "nav.admin": "Admin",
    "nav.login": "Se connecter", "nav.register": "S'inscrire",
    "nav.search_placeholder": "Rechercher un pilote, un produit...",
    "hero.tagline": "Toute la merch de vos pilotes préférés, au même endroit.",
    "hero.title": "La merch\nqui parle\nplus fort.",
    "hero.desc": "Découvrez la collection officielle de vos pilotes et écuries préférés. Karting, F1, NASCAR, rallye, dragster, motocross, terre battue. Expédié directement par les pilotes.",
    "hero.search_placeholder": "Cherchez un pilote, un hoodie, une casquette...",
    "hero.discover": "Découvrir", "hero.sell": "Vendre ma merch",
    "cta.artists_title": "De la piste,\nà la rue.",
    "cta.artists_desc": "Créez votre studio en 5 minutes. Publiez vos produits, gérez vos commandes, encaissez. Recevez vos paiements sécurisés — directement auprès de vos fans.",
    "cta.open_studio": "Ouvrir mon studio",
    "footer.shop": "Boutique", "footer.sellers": "Vendeurs", "footer.contact": "Contact",
    "prefs.title": "Bienvenue sur Merch Hub",
    "prefs.subtitle": "Choisissez votre langue et devise préférées",
    "prefs.language": "Langue", "prefs.currency": "Devise",
    "prefs.confirm": "Continuer",
  },
  en: {
    "nav.catalog": "Catalog", "nav.artists": "Pilots", "nav.hoodies": "Hoodies",
    "nav.tshirts": "T-Shirts", "nav.limited": "Limited Ed.",
    "nav.orders": "Orders", "nav.studio": "Studio", "nav.admin": "Admin",
    "nav.login": "Sign in", "nav.register": "Sign up",
    "nav.search_placeholder": "Search for a pilot, a product...",
    "hero.tagline": "All the merch from your favorite pilots, in one place.",
    "hero.title": "Merch that\nspeaks\nlouder.",
    "hero.desc": "Discover the official collection of your favorite pilots and racing teams. Hoodies, tees, limited editions. Shipped directly by the pilots.",
    "hero.search_placeholder": "Search for a pilot, a hoodie, a cap...",
    "hero.discover": "Explore", "hero.sell": "Sell my merch",
    "cta.artists_title": "Your merch.\nYour fans.",
    "cta.artists_desc": "Set up your studio in 5 minutes. Publish products, manage orders, get paid. Receive secure payments directly from your fans.",
    "cta.open_studio": "Open my studio",
    "footer.shop": "Shop", "footer.sellers": "Sellers", "footer.contact": "Contact",
    "prefs.title": "Welcome to Merch Hub",
    "prefs.subtitle": "Choose your preferred language and currency",
    "prefs.language": "Language", "prefs.currency": "Currency",
    "prefs.confirm": "Continue",
  },
};

export function useT() {
  const { language } = usePrefs();
  return (key) => T[language]?.[key] ?? T.fr[key] ?? key;
}
